import type Ad from "../gpt/Ad";
import type GPT from "../gpt/GPT";
import Overlayer from "../util/Overlayer";
import { type AdSizes } from "../viewability/Viewability";

const formatTimestamp = (ts: number) => `${ Math.floor(ts) }ms`;

const LIST_STYLE = "list-style-type: disc; padding-left: 20px; margin: 4px 0 0;";

function unrenderedMessage() {
    return ["GPT render event not received for this ad.",
        "Either the request has not been sent out or",
        "the response has not yet been received."].join("<br/>");
}

interface AdMetrics {
    adCode: string;
    sizes: AdSizes;
    empty: boolean;
    creativeId: string | number | null;
    lineItemId: string | number | null;
    // time taken to make ad slot request
    timeToRequest: number;
    // time taken to respond to ad slot request
    timeToRespond: number;
    // total time from Sparkle.init() -> rendered ad
    timeToRender: number;
}

function renderMessage(metrics: AdMetrics | null): string {
    if (!metrics) {
        return unrenderedMessage();
    }

    return [
        `AdCode: ${ metrics.adCode }`,
        `Empty?: ${ metrics.empty }`,
        `Creative ID: ${ metrics.creativeId }`,
        `Line Item ID: ${ metrics.lineItemId }`,
        `Time To Request: ${ formatTimestamp(metrics.timeToRequest) }`,
        `Time To Respond: ${ formatTimestamp(metrics.timeToRespond) }`,
        `Time To Render: ${ formatTimestamp(metrics.timeToRender) }`,
        `Sizes: [${ metrics.sizes }]`
    ].join("<br/>");
}

/**
 * This is util function which takes in two functions of getting
 * targeting keys and targeting info, parses them, and return back
 * a formated html string
 * @param {Function} getTargetingKeys
 * @param {Function} getTargeting
 * @returns {String}
 */
function targetingListUtil(
    getTargetingKeys: () => string[],
    getTargeting: (key: string) => string | string[]
) {
    const targetingListString = getTargetingKeys().reduce((output, key) => `${ output }<li>${ key }: ${ getTargeting(key) }</li>`, "");

    if (!targetingListString) {
        return "";
    }

    return `<ul style="${ LIST_STYLE }">${ targetingListString }</ul>`;
}

function pageTargetingList(gpt: GPT): string {
    if (!gpt) {
        return "";
    }

    return targetingListUtil(gpt.getTargetingKeys.bind(gpt), gpt.getTargeting.bind(gpt));
}

function targetingList(ad: Ad): string {
    const targeting = ad.getTargeting();
    const targetingListString = Object.keys(targeting).reduce((output, key) => `${ output }<li>${ key }: ${ targeting[key] }</li>`, "");

    return `<ul style="${ LIST_STYLE }">${ targetingListString }</ul>`;
}

function slotTargetingList(ad: Ad): string {
    const slot = ad.getSlot();

    if (!slot) {
        return "";
    }

    const targeting = slot.getConfig("targeting").targeting ?? {};

    return targetingListUtil(() => Object.keys(targeting), (key) => targeting[key] ?? []);
}

/**
 * @class AdDebug
 */
class AdDebug {

    /**
     * Shows debug information for a particular ad
     */
    static show(startTime: number, ad: Ad, gpt: GPT) {
        const adElement = ad.getElement();
        const adWasRemovedFromDom = !adElement;

        if (!ad.isDisplayable() || adWasRemovedFromDom) {
            return;
        }

        const metrics = AdDebug.metrics(startTime, ad);
        const targeting = targetingList(ad);
        const slotTargeting = slotTargetingList(ad);
        const pageTargeting = pageTargetingList(gpt);

        const content = renderMessage(metrics) + slotTargeting + targeting + pageTargeting;

        Overlayer.overlay({
            content,
            adElement,
            empty: !metrics || metrics.empty
        });
    }

    static metrics(startTime: number, ad: Ad): AdMetrics | null {
        const event = ad.getSlotRenderedEvent();

        if (!event) {
            return null;
        }

        const timeToRequest = ad.getRequestTime() as number - startTime;
        const timeToRender = ad.getRenderTime() as number - startTime;
        const timeToRespond = timeToRender - timeToRequest;

        return {
            adCode: ad.getUnitName(),
            sizes: ad.getSizes(),
            empty: event.isEmpty(),
            creativeId: event.getCreativeId(),
            lineItemId: event.getLineItemId(),
            timeToRequest,
            timeToRespond,
            timeToRender
        };
    }

}

export default AdDebug;
